/**
 * clubSetupFormContext.js — FILL_FORM descriptor do ClubSetupWizardPage
 * (workflow SETUP_CLUB, 26/08/2026).
 *
 * Decisões de arquitetura (fechadas com o gestor do projeto):
 *  1. form_context reflete a ETAPA ATUAL do wizard — nunca despeja as
 *     2.242 linhas de estado no prompt. current_step é o nome da etapa.
 *  2. Patch de COLEÇÃO é ATÔMICO: uma entrada inválida rejeita o array
 *     inteiro, apontando exatamente qual entrada e por quê. Aplicar ≠ salvar
 *     — o save continua sendo o botão normal do wizard (startOnboarding etc).
 *  3. IDs vêm do backend/estado do usuário — o LLM nunca inventa identidade.
 */
// Espelho de CATEGORY_OPTIONS do onboardingService (import direto puxaria
// api.js/axios — quebra jest). Mesmo padrão do WELLNESS_BODY_PARTS.
const CATEGORY_OPTIONS = [
  { value: 'mirim', label: 'Mirim (até 10)' },
  { value: 'pre_mirim', label: 'Pré-mirim (11-12)' },
  { value: 'infantil', label: 'Infantil (13-14)' },
  { value: 'juvenil', label: 'Juvenil (15-16)' },
  { value: 'junior', label: 'Junior (17-18)' },
  { value: 'adulto', label: 'Adulto / Sênior' },
  { value: 'master', label: 'Master (25+)' },
];

export const CLUB_SETUP_STAFF_ROLES = [
  'TREINADOR', 'PREPARADOR_FISICO', 'FISIOTERAPEUTA',
  'NUTRICIONISTA', 'MEDICO', 'ANALISTA',
];

const BRAZIL_STATES = [
  'AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT',
  'PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO',
];

// 1..8 → nome da etapa (staff_scope é derivado: staff sem categorias)
const STEP_NAMES = {
  1: 'identity', 2: 'contacts', 3: 'categories', 4: 'staff',
  5: 'roster', 6: 'plan', 7: 'invites', 8: 'review',
};

/** Normaliza categoria: aceita value ('juvenil') OU label ('Juvenil (15-16)' / 'juvenil (15'). */
function normalizeCategory(input) {
  if (typeof input !== 'string' || !input.trim()) return null;
  const low = input.trim().toLowerCase();
  for (const opt of CATEGORY_OPTIONS) {
    if (low === opt.value) return opt.value;
    const labelBase = opt.label.toLowerCase().split('(')[0].trim().replace(/[-\s]/g, '_');
    if (low.replace(/[-\s]/g, '_') === labelBase) return opt.value;
    if (opt.label.toLowerCase().startsWith(low) && low.length >= 4) return opt.value;
  }
  return null;
}

/**
 * form_context fatiado por etapa. state = slices do wizard.
 * @param {{currentStep:number, formData:object, sports:Array, staffMembers:Array,
 *          rosterAthletes:Array, planSelection:object, invites:Array}} state
 */
export function buildClubSetupFormContext(state = {}) {
  const {
    currentStep = 1, formData = {}, sports = [],
    staffMembers = [], rosterAthletes = [], planSelection = {}, invites = [],
  } = state;

  let step = STEP_NAMES[currentStep] || 'identity';
  const categories = (sports[0]?.categories || []).slice();
  // staff cadastrado mas sem vínculo de categoria → etapa lógica staff_scope
  if (step === 'staff' && staffMembers.length > 0
      && staffMembers.some((s) => !(s.category_ids?.length))) {
    step = 'staff_scope';
  }

  const missing = [];
  const values = { categories };

  switch (step) {
    case 'identity':
      values.legal_name = formData.legal_name || '';
      values.city = formData.city || '';
      values.state = formData.state || '';
      values.org_type = formData.org_type || 'club';
      if (!formData.legal_name) missing.push('legal_name');
      if (!formData.city) missing.push('city');
      if (!formData.state) missing.push('state');
      break;
    case 'categories':
      if (categories.length === 0) missing.push('categories');
      break;
    case 'staff':
    case 'staff_scope':
      values.staff = staffMembers.map((s) => ({
        full_name: s.full_name, staff_role: s.staff_role,
        category_ids: s.category_ids || [],
      }));
      if (staffMembers.length === 0) missing.push('staff');
      else if (step === 'staff_scope') missing.push('staff_scope');
      break;
    case 'roster':
      values.roster_count = rosterAthletes.length;
      break;
    case 'plan':
      values.plan = planSelection.family
        ? { family: planSelection.family, athletes: planSelection.athletes }
        : null;
      if (!planSelection.family) missing.push('plan');
      break;
    case 'invites':
      values.invites_count = invites.length;
      break;
    case 'review':
      values.staff = staffMembers.map((s) => ({ full_name: s.full_name, staff_role: s.staff_role }));
      values.roster_count = rosterAthletes.length;
      if (categories.length === 0) missing.push('categories');
      if (!formData.legal_name) missing.push('legal_name');
      break;
    default:
      break;
  }

  return {
    form_id: 'club_setup',
    schema_version: 'club_setup.v1',
    current_step: step,
    current_values: values,
    missing_required_fields: missing,
    allowed_values: {
      categories: CATEGORY_OPTIONS.map((c) => c.value),
      staff_role: CLUB_SETUP_STAFF_ROLES,
      state: 'UF (2 letras)',
    },
  };
}

/**
 * Aplica patch do agente sobre o estado do wizard — ATÔMICO por coleção.
 * Retorna { changes, applied, rejected }: quem consome faz os setState
 * correspondentes (changes.formData / changes.categories / changes.staff).
 * Nunca salva. Nunca apaga campos não mencionados.
 */
export function applyClubSetupPatch(state, patch) {
  const applied = [];
  const rejected = [];
  const changes = {};

  if (!patch || typeof patch !== 'object') {
    return { changes, applied, rejected: [{ field: '*', reason: 'patch inválido' }] };
  }

  for (const [field, value] of Object.entries(patch)) {
    if (['legal_name', 'trade_name', 'city', 'website', 'instagram'].includes(field)) {
      if (typeof value === 'string' && value.trim()) {
        changes.formData = { ...(changes.formData || {}), [field]: value.trim() };
        applied.push(field);
      } else rejected.push({ field, reason: 'texto vazio ou inválido' });

    } else if (field === 'state') {
      const uf = String(value || '').trim().toUpperCase();
      if (BRAZIL_STATES.includes(uf)) {
        changes.formData = { ...(changes.formData || {}), state: uf };
        applied.push(field);
      } else rejected.push({ field, reason: `UF desconhecida: "${value}"` });

    } else if (field === 'categories') {
      // BULK ATÔMICO: uma entrada inválida → array inteiro recusado.
      if (!Array.isArray(value) || value.length === 0) {
        rejected.push({ field, reason: 'esperado array não-vazio de categorias' });
        continue;
      }
      const normalized = [];
      let bad = null;
      for (const raw of value) {
        const v = normalizeCategory(raw);
        if (!v) { bad = raw; break; }
        if (!normalized.includes(v)) normalized.push(v);
      }
      if (bad !== null) {
        rejected.push({
          field,
          reason: `categoria inválida: "${bad}" — nenhuma categoria foi aplicada. `
            + `Válidas: ${CATEGORY_OPTIONS.map((c) => c.value).join(', ')}`,
        });
      } else {
        changes.categories = normalized;
        applied.push(field);
      }

    } else if (field === 'staff') {
      // BULK ATÔMICO de profissionais.
      if (!Array.isArray(value) || value.length === 0) {
        rejected.push({ field, reason: 'esperado array não-vazio de profissionais' });
        continue;
      }
      const clean = [];
      let err = null;
      value.forEach((m, i) => {
        if (err) return;
        if (!m || typeof m !== 'object' || !String(m.full_name || '').trim()) {
          err = `entrada #${i + 1}: full_name obrigatório`;
          return;
        }
        const role = String(m.staff_role || 'TREINADOR').toUpperCase();
        if (!CLUB_SETUP_STAFF_ROLES.includes(role)) {
          err = `entrada #${i + 1} (${m.full_name}): staff_role inválido "${m.staff_role}"`;
          return;
        }
        clean.push({
          full_name: String(m.full_name).trim(),
          email: typeof m.email === 'string' ? m.email.trim() : '',
          phone: typeof m.phone === 'string' ? m.phone.trim() : '',
          staff_role: role,
          category_ids: [],
        });
      });
      if (err) {
        rejected.push({ field, reason: `${err} — nenhum profissional foi aplicado` });
      } else {
        changes.staff = clean;
        applied.push(field);
      }

    } else {
      rejected.push({ field, reason: 'campo desconhecido no schema club_setup.v1' });
    }
  }

  return { changes, applied, rejected };
}
